import { useMemo } from "react";

const SVG_NS = "http://www.w3.org/2000/svg";

/** Cache of already registered definitions: source svg markup -> generated `<svg><use/></svg>` markup */
const referenceMarkupBySource = new Map<string, string>();

/** Lazily created hidden `<svg>` on document.body holding one `<symbol>` per registered source svg */
let definitionsContainer: SVGSVGElement | null = null;

/**
 * djb2 string hash producing a short, stable id fragment for a given svg markup
 */
const hashString = (value: string): string => {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(36);
};

/**
 * Returns the shared definitions container, (re)creating it when missing or detached.
 * The cache is cleared together with recreation so references never point to missing symbols.
 */
const getDefinitionsContainer = (): SVGSVGElement => {
  if (definitionsContainer === null || !definitionsContainer.isConnected) {
    referenceMarkupBySource.clear();
    definitionsContainer = document.createElementNS(SVG_NS, "svg");
    definitionsContainer.setAttribute("data-svg-use-definitions", "");
    definitionsContainer.setAttribute("aria-hidden", "true");
    definitionsContainer.setAttribute("focusable", "false");
    // Hidden, but not via display:none - that breaks <use> rendering of referenced content in some browsers
    definitionsContainer.style.position = "absolute";
    definitionsContainer.style.width = "0";
    definitionsContainer.style.height = "0";
    definitionsContainer.style.overflow = "hidden";
    document.body.appendChild(definitionsContainer);
  }
  return definitionsContainer;
};

/**
 * Parses an svg markup string and returns its root `<svg>` element, or null when the markup has no root svg
 */
const parseSvgRoot = (markup: string): SVGSVGElement | null => {
  const host = document.createElement("div");
  host.innerHTML = markup;
  const root = host.firstElementChild;
  return root instanceof SVGSVGElement ? root : null;
};

/**
 * Registers the source svg as a hidden `<symbol>` (once per distinct markup) and returns the markup
 * of a `<svg><use href="#..."/></svg>` reference carrying all attributes of the source `<svg>`.
 */
const registerSvgDefinition = (source: SVGSVGElement, sourceMarkup: string): string => {
  const container = getDefinitionsContainer();
  const id = `svg-def-${hashString(sourceMarkup)}`;

  const symbol = document.createElementNS(SVG_NS, "symbol");
  symbol.id = id;
  const viewBox = source.getAttribute("viewBox");
  if (viewBox !== null) {
    symbol.setAttribute("viewBox", viewBox);
  }
  for (const child of Array.from(source.childNodes)) {
    symbol.appendChild(child.cloneNode(true));
  }
  container.appendChild(symbol);

  const reference = document.createElementNS(SVG_NS, "svg");
  for (const attribute of Array.from(source.attributes)) {
    // The source id must not be duplicated on every reference; the generated symbol id replaces it
    if (attribute.name !== "id") {
      reference.setAttribute(attribute.name, attribute.value);
    }
  }
  const use = document.createElementNS(SVG_NS, "use");
  use.setAttribute("href", `#${id}`);
  if (viewBox !== null) {
    // The use shadow viewport defaults to x=0 y=0, which only matches viewBoxes with a "0 0" origin.
    // For a non-zero origin (e.g. Material Symbols' "0 -960 960 960") the content would land outside
    // the visible region, so span the use over exactly the viewBox rectangle instead.
    const [minX, minY, width, height] = viewBox.trim().split(/[\s,]+/);
    if (minX !== undefined && minY !== undefined && width !== undefined && height !== undefined) {
      use.setAttribute("x", minX);
      use.setAttribute("y", minY);
      use.setAttribute("width", width);
      use.setAttribute("height", height);
    }
  }
  reference.appendChild(use);

  const referenceMarkup = reference.outerHTML;
  referenceMarkupBySource.set(sourceMarkup, referenceMarkup);
  return referenceMarkup;
};

/**
 * Turns an inline svg (element or markup string) into a lightweight `<svg><use href="#..."/></svg>` reference
 * of the same type as the input: an SVGElement in, a fresh SVGElement out; a string in, a string out.
 *
 * The source svg content is registered once as a `<symbol>` inside a hidden container on document.body,
 * so the same icon repeated many times on a page (e.g. timeline items) pays for one DOM definition only;
 * every occurrence is just a tiny `<use>` reference. The returned reference keeps all attributes of the
 * source `<svg>` (width, height, class, fill, viewBox, ...) so it renders identically.
 *
 * Inputs that are not a root `<svg>` element are returned unchanged as a graceful fallback.
 * Definitions live for the lifetime of the page; there is no unregistration.
 */
export function getSvgUseReference(svg: SVGElement): SVGElement;
export function getSvgUseReference(svg: string): string;
export function getSvgUseReference(svg: SVGElement | string): SVGElement | string;
export function getSvgUseReference(svg: SVGElement | string): SVGElement | string {
  // Keep the container fresh before consulting the cache (it clears the cache when recreated)
  getDefinitionsContainer();

  const sourceMarkup = typeof svg === "string" ? svg.trim() : svg.outerHTML;

  let referenceMarkup = referenceMarkupBySource.get(sourceMarkup);
  if (referenceMarkup === undefined) {
    const source = typeof svg === "string" ? parseSvgRoot(sourceMarkup) : svg;
    if (!(source instanceof SVGSVGElement)) {
      return typeof svg === "string" ? sourceMarkup : svg;
    }
    referenceMarkup = registerSvgDefinition(source, sourceMarkup);
  }

  if (typeof svg === "string") {
    return referenceMarkup;
  }
  // Every element call site needs its own node instance, so build a fresh one from the cached markup
  return parseSvgRoot(referenceMarkup) ?? svg;
}

/**
 * Hook variant of {@link getSvgUseReference}: memoizes the `<use>` reference for the given svg,
 * returning the same type as the input. Returns null when no svg is provided.
 */
export function useSvgReference(svg: SVGElement): SVGElement;
export function useSvgReference(svg: string): string;
export function useSvgReference(svg: SVGElement | string | null | undefined): SVGElement | string | null;
export function useSvgReference(svg: SVGElement | string | null | undefined): SVGElement | string | null {
  return useMemo(() => (svg === null || svg === undefined ? null : getSvgUseReference(svg)), [svg]);
}